import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * File uploader. `value` = array of FileRef objects, OR a single FileRef when single=true.
 * onChange(newList) or onChange(newFile) respectively.
 */
export const FileUpload = ({ value, onChange, testid = "file-upload", accept = "image/*,application/pdf", label, single = false }) => {
    const [uploading, setUploading] = useState(false);
    const list = single ? (value ? [value] : []) : (value || []);
    const defaultLabel = label || (single ? "Upload file" : "Upload files");

    const handle = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploading(true);
        try {
            const uploaded = [];
            for (const f of (single ? files.slice(0, 1) : files)) {
                const fd = new FormData();
                fd.append("file", f);
                const { data } = await api.post("/uploads", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                uploaded.push(data);
            }
            if (single) onChange(uploaded[0]);
            else onChange([...(value || []), ...uploaded]);
            toast.success(`Uploaded ${uploaded.length} file${uploaded.length > 1 ? "s" : ""}`);
        } catch (err) {
            console.error(err);
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const remove = (idx) => {
        if (single) { onChange(null); return; }
        const next = [...value];
        next.splice(idx, 1);
        onChange(next);
    };

    const hideUploader = single && list.length >= 1;

    return (
        <div className="space-y-2">
            {!hideUploader && (
                <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-stone-300 hover:border-stone-500 text-stone-500 hover:text-stone-800 cursor-pointer text-sm transition-colors" data-testid={`${testid}-label`}>
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" strokeWidth={1.5} />}
                    <span>{uploading ? "Uploading…" : defaultLabel}</span>
                    <input
                        type="file"
                        className="hidden"
                        onChange={handle}
                        accept={accept}
                        multiple={!single}
                        disabled={uploading}
                        data-testid={`${testid}-input`}
                    />
                </label>
            )}
            {list.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {list.map((f, i) => (
                        <div key={f.id || i} className="flex items-center gap-2 border border-stone-200 bg-white px-2 py-1 text-xs">
                            <span className="text-stone-500 truncate max-w-[180px]" title={f.original_filename}>{f.original_filename}</span>
                            <button type="button" onClick={() => remove(i)} className="text-stone-500 hover:text-rose-600" data-testid={`${testid}-remove-${i}`}>
                                <X className="h-3 w-3" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default FileUpload;
